// fleet-generated: do not edit by hand
//
// Rendered from    : infra/fleet/artifacts/typography/queries.tmpl.ts
// Parameters       : infra/fleet/registry/repos.ts  (entry "vigia-fleet-app")
// Destination      : packages/fleet/typography/src/queries.ts
//
// TO CHANGE THIS FILE, edit the template or the registry entry in infra and
// re-render. Editing it here is reverted by the next render, and the guard
// in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo vigia-fleet-app
//     node fleet/fleet.ts check --repo vigia-fleet-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import {
  and,
  asc,
  eq,
  font_family,
  font_variant,
  type FontFamilyRecord,
  type FontVariantRecord,
  or,
  type PowerSyncDrizzleDB,
} from "@vigia/powersync";

export interface FontFamilyWithVariants extends FontFamilyRecord {
  variants: readonly FontVariantRecord[];
}

/**
 * Gets all active font families ordered by sort_order and name.
 */
export function getFontFamiliesQuery(db: PowerSyncDrizzleDB, category?: string) {
  const filters = [eq(font_family.is_active, 1)];
  if (category) {
    filters.push(eq(font_family.category, category));
  }

  return db
    .select()
    .from(font_family)
    .where(and(...filters))
    .orderBy(asc(font_family.sort_order), asc(font_family.name));
}

/**
 * Gets all font variants ordered by weight.
 */
export function getFontVariantsQuery(db: PowerSyncDrizzleDB) {
  return db.select().from(font_variant).orderBy(asc(font_variant.weight));
}

/**
 * Cheap presence probe: is the synced font catalog available locally at all?
 * Returns at most one active family row. Used to tell "the catalog hasn't synced
 * yet" (empty) apart from "the chosen font was removed" (catalog present, family
 * absent) so a valid choice is never falsely flagged unavailable before sync.
 */
export function getFontCatalogPresenceQuery(db: PowerSyncDrizzleDB) {
  return db
    .select({ id: font_family.id })
    .from(font_family)
    .where(eq(font_family.is_active, 1))
    .limit(1);
}

/**
 * Gets a specific font family by id or family name.
 */
export function getFontFamilyQuery(db: PowerSyncDrizzleDB, fontId: string | null) {
  return db
    .select()
    .from(font_family)
    .where(
      and(
        eq(font_family.is_active, 1),
        or(eq(font_family.id, fontId ?? ""), eq(font_family.family, fontId ?? "")),
      ),
    )
    .limit(1);
}

/**
 * Gets font variants for a specific resolved family ID.
 */
export function getFontFamilyVariantsQuery(
  db: PowerSyncDrizzleDB,
  resolvedFamilyId: string | null,
) {
  return db
    .select()
    .from(font_variant)
    .where(eq(font_variant.font_family_id, resolvedFamilyId ?? ""))
    .orderBy(asc(font_variant.weight));
}

/**
 * Pure function to group font families with their variants.
 */
export function groupFontsWithVariants(
  familiesData: readonly FontFamilyRecord[],
  variantsData: readonly FontVariantRecord[],
): FontFamilyWithVariants[] {
  const variantsByFamily = new Map<string, FontVariantRecord[]>();
  for (const variant of variantsData) {
    const familyId = variant.font_family_id;
    if (!familyId) {
      continue;
    }
    const existing = variantsByFamily.get(familyId) ?? [];
    existing.push(variant);
    variantsByFamily.set(familyId, existing);
  }

  return familiesData.map((family) => {
    const variants = variantsByFamily.get(family.id) ?? [];
    return { ...family, variants };
  });
}

/**
 * Resolves a font ID from family data, supporting backward compatibility with family names.
 */
export function resolveFontId(
  familyData: readonly FontFamilyRecord[],
  requestedId: string | null,
): string {
  const [first] = familyData;
  if (!first) {
    return requestedId ?? "";
  }
  // If we found a match by family name, return the real ID.
  // This allows canonical queries for variants to work.
  if (first.id === requestedId || first.family === requestedId) {
    return first.id;
  }
  return requestedId ?? "";
}

/**
 * Maps raw family and variants data into a FontFamilyWithVariants object.
 */
export function mapFontFamilyWithVariants(
  familyData: readonly FontFamilyRecord[],
  variantsData: readonly FontVariantRecord[] | undefined,
): FontFamilyWithVariants | null {
  const [first] = familyData;
  if (!first) {
    return null;
  }

  return {
    ...first,
    variants: variantsData ?? [],
  };
}
